import { useCallback, useEffect, useMemo } from 'react'
import { useSetAtom } from 'jotai'
import { useIsAudioNotePaused, useIsRecordingAudioNote } from '@renderer/hooks/audio/use-audio-note-selectors'
import AudioNoteIpcRenderer from '@renderer/ipc/AudioNoteIpc.renderer'
import { sidepanelOpenFocusIntentAtom, useSetSidepanelState, useSidepanelState } from '@renderer/overlay/atoms'
import { trackSidepanelClosed, trackSidepanelOpened } from '@renderer/overlay/utils/track-sidepanel'
import { useSetSidepanelHotkeyOpened } from '@renderer/overlay/Sidepanel/atoms'
import { useViewActions, useViewNavigation } from '@renderer/overlay/Sidepanel/view-atoms'
import { useActiveNotchToast } from '@renderer/overlay/Toaster/use-active-notch-toast'
import { isLiveMeetingView } from '@renderer/overlay/Sidepanel/components/meeting/meeting-view-id'
import { useLiveMeetingIdentity } from '@renderer/overlay/Sidepanel/components/meeting/use-live-meeting-identity'
import useTriggerHotkey from '@renderer/hooks/useTriggerHotkey'
import { useAuthStatus } from '@renderer/hooks/use-auth-status'
import { PillAudioNoteControls } from './audio-note/pill-audio-note-controls'
import { useAudioNotePhase } from './audio-note/use-audio-note-phase'
import { useMiniNotched, useMiniVisibility, useSetMiniContentHovered, useSetMiniHovered } from './atoms'
import { Mini as MiniRoot } from './components/mini'
import { MiniNotched } from './mini-notched'
import { MiniQuickActions } from './components/mini/nested/quick-actions'
import { isMac } from '@shared/utils/platform'
import { useSetupCallOffer } from './setup-call/use-setup-call-offer'

export function MiniOverlay() {
  // Only 'hidden' hides the notch; a Mini set to show in meetings shows always
  const isMiniShown = useMiniVisibility() !== 'hidden'
  const isRecording = useIsRecordingAudioNote()
  const isPaused = useIsAudioNotePaused()
  const phase = useAudioNotePhase()
  const setSidepanelState = useSetSidepanelState()
  const setHotkeyOpened = useSetSidepanelHotkeyOpened()
  const setFocusIntent = useSetAtom(sidepanelOpenFocusIntentAtom)
  const { closeAllViews } = useViewActions()
  const sidepanelState = useSidepanelState()
  const { topView, canGoBack } = useViewNavigation()
  const liveMeeting = useLiveMeetingIdentity()
  const setMiniHovered = useSetMiniHovered()
  const setMiniContentHovered = useSetMiniContentHovered()
  const triggerHotkey = useTriggerHotkey()
  const [isNotched, setNotched] = useMiniNotched()
  const activeNotchToast = useActiveNotchToast()
  const isSignedOut = useAuthStatus() === 'signedOut'
  const isMeetingActive = phase !== 'idle'
  const setupCall = useSetupCallOffer(phase)

  const handleLogoClick = useCallback(() => {
    if (phase === 'finished') {
      AudioNoteIpcRenderer.clearAudioNoteState()
    }

    const isOpen = sidepanelState === 'open'

    if (!isOpen) {
      trackSidepanelOpened('mini')
      setFocusIntent('chat')
      setHotkeyOpened(true)
      setSidepanelState('open')
      closeAllViews()
    } else if (canGoBack) {
      setFocusIntent('chat')
      closeAllViews()
    } else {
      trackSidepanelClosed('mini')
      setSidepanelState('hidden')
      setHotkeyOpened(false)
    }
  }, [phase, sidepanelState, canGoBack, setSidepanelState, setHotkeyOpened, setFocusIntent, closeAllViews])

  const isMeetingViewOpen = sidepanelState === 'open' && isLiveMeetingView(topView, liveMeeting)
  const hasNotchToast = isNotched && activeNotchToast !== null
  const hasSetupCall = isNotched && setupCall.moment !== null
  const shouldShow =
    (hasNotchToast || hasSetupCall || isMiniShown) &&
    !(isMeetingActive && isMeetingViewOpen && !isNotched) &&
    !(isNotched && isSignedOut)

  // Mac docks the Mini; set here because a hidden Mini never mounts, and persisted so main's window level follows
  useEffect(() => {
    if (isMac && !isNotched) setNotched(true)
  }, [isNotched, setNotched])

  useEffect(() => {
    setMiniHovered(false)
    setMiniContentHovered(false)
  }, [setMiniHovered, setMiniContentHovered])

  useEffect(() => {
    if (!shouldShow) {
      setMiniHovered(false)
      setMiniContentHovered(false)
    }
  }, [shouldShow, setMiniHovered, setMiniContentHovered])

  const logo = useMemo(
    () => (
      <MiniRoot.Logo
        onClick={handleLogoClick}
        triggerHotkey={triggerHotkey}
        showAudioNoteUI={isMeetingActive}
        hideLabel={isNotched}
        className={isNotched ? 'size-7 p-0 [&_svg]:size-4' : undefined}
      />
    ),
    [handleLogoClick, triggerHotkey, isMeetingActive, isNotched],
  )

  if (!shouldShow) {
    return null
  }

  return (
    <MiniRoot isRecording={isRecording} isPaused={isPaused}>
      {isNotched ? (
        <MiniNotched onLogoClick={handleLogoClick} phase={phase} setupCall={setupCall} />
      ) : (
        <MiniRoot.Content>
          {logo}
          {phase === 'idle' ? (
            <>
              <MiniQuickActions />
              <MiniRoot.Menu />
            </>
          ) : (
            <PillAudioNoteControls phase={phase} />
          )}
        </MiniRoot.Content>
      )}
    </MiniRoot>
  )
}

export { MiniOverlay as Mini }
